import React, { useEffect, useMemo, useState } from "react";
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaUserPlus,
  FaPhoneAlt,
  FaFileExcel,
  FaEye,
} from "react-icons/fa";
import axiosInstance from "../api/axiosInstance";
import AddEditEmployeeMaster from "./AddEditEmployeeMaster";
import AddeditQuickAdd from "./AddeditQuickAdd";
import EmployeePreviewData from "./EmployeePrivewdata";
import AddEditEmployeeOptionalFields from "./AddEditEmployeeOptionalFields";

const EmployeeMaster = () => {

  /* ================= SAFE USER ================= */

  let storedUser = {};
  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch { }

  const groupId = storedUser?.groupId;

  /* ================= STATE ================= */

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [deptMap, setDeptMap] = useState({});
  const [desigMap, setDesigMap] = useState({});
  const [restMap, setRestMap] = useState({});

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [quickOpen, setQuickOpen] = useState(false);
  const [optionalOpen, setOptionalOpen] = useState(false);
  const [optionalData, setOptionalData] = useState(null);

  /* ================= FETCH EMPLOYEES ================= */

  const fetchEmployees = async () => {
    try {
      setLoading(true);

      const res = await axiosInstance.get(
        `EmployeeMaster/GetByGroup?groupId=${groupId}`
      );

      setData(res.data || []);

    } catch (err) {
      console.error("Employee fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= FETCH MASTERS ================= */

  const fetchMasters = async () => {
    try {

      const [deptRes, desigRes, restRes] = await Promise.all([
        axiosInstance.get(`/DepartmentMaster/GetByGroup?groupId=${groupId}`),
        axiosInstance.get(`/DesignationMaster/GetByGroup?groupId=${groupId}`),
        axiosInstance.get(`/RestaurantMaster/GetByGroup?groupId=${groupId}`),
      ]);

      const dept = {};
      (deptRes.data || []).forEach((d) => {
        dept[d.departmentId] = d.departmentName;
      });

      const desig = {};
      (desigRes.data || []).forEach((d) => {
        desig[d.designationId] = d.designationName;
      });

      const rest = {};
      (restRes.data || []).forEach((r) => {
        rest[r.restId] = r.restaurantName;
      });

      setDeptMap(dept);
      setDesigMap(desig);
      setRestMap(rest);

    } catch (err) {
      console.error("Master load error:", err);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchMasters();
  }, []);

  /* ================= SEARCH ================= */

  const filteredData = useMemo(() => {

    const term = search.toLowerCase().trim();
    if (!term) return data;

    return data.filter(
      (item) =>
        item.empName?.toLowerCase().includes(term) ||
        item.empMobileNo?.includes(term)
    );

  }, [data, search]);

  /* ================= ACTIONS ================= */

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormOpen(true);
  };

  const openOptionalFields = (row) => {
    setOptionalData(row);
    setOptionalOpen(true);
  };

  const handleDelete = async (row) => {

    if (!window.confirm("Delete this employee?")) return;

    try {
      await axiosInstance.delete(`/EmployeeMaster/${row.empId}`);
      fetchEmployees();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Delete failed");
    }
  };

  /* ================= EXCEL ================= */

  const exportExcel = () => {

    if (!filteredData.length) return;

    const rows = filteredData.map((r) => ({
      EmpId: r.empId,
      Name: `"${r.empName || ""}"`,
      Mobile: r.empMobileNo,
      Department: deptMap[r.departmentId] || "",
      Designation: desigMap[r.designationId] || "",
      Restaurant: restMap[r.restId] || "",
      Type: r.empType,
      Status: r.empStatus,
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map((e) => Object.values(e).join(",")),
      ].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = "EmployeeMaster.csv";
    link.click();
  };

  /* ================= UI ================= */

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 md:p-5 flex flex-col border border-slate-200 min-h-[calc(100vh-120px)]">

      {/* HEADER */}

      <div className="flex flex-col md:flex-row justify-between items-center gap-3 mb-4">

        <h2 className="text-xl font-bold">
          Employee Master
        </h2>

        <div className="flex flex-wrap gap-2">

          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search employee..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border rounded-lg"
            />
          </div>

          <button
            onClick={exportExcel}
            className="bg-green-600 text-white px-3 py-2 rounded-lg flex items-center gap-2"
          >
            <FaFileExcel /> Excel
          </button>

          <button
            onClick={openAdd}
            className="bg-blue-600 text-white px-3 py-2 rounded-lg flex items-center gap-2"
          >
            <FaPlus /> Add
          </button>

          <button
            onClick={() => setQuickOpen(true)}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm"
          >
            ⚡ Quick Add
          </button>

        </div>

      </div>

      {/* ================= DESKTOP TABLE ================= */}

      <div className="hidden md:block overflow-auto border rounded-lg">

        <table className="w-full text-sm">

          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 border">Emp Id</th>
              <th className="p-2 border">Name</th>
              <th className="p-2 border">Mobile</th>
              <th className="p-2 border">Restaurant</th>
              <th className="p-2 border">Department</th>
              <th className="p-2 border">Designation</th>
              <th className="p-2 border">Type</th>
              <th className="p-2 border">Status</th>
              <th className="p-2 border">Action</th>
            </tr>
          </thead>

          <tbody>

            {loading ? (
              <tr>
                <td colSpan="9" className="text-center p-6">
                  Loading...
                </td>
              </tr>
            ) : filteredData.map((row) => (

              <tr key={row.empId} className="hover:bg-gray-50">

                <td className="p-2 border">{row.empId}</td>
                <td className="p-2 border font-medium">{row.empName}</td>
                <td className="p-2 border">{row.empMobileNo}</td>
                <td className="p-2 border">{restMap[row.restId] || "-"}</td>
                <td className="p-2 border">{deptMap[row.departmentId] || "-"}</td>
                <td className="p-2 border">{desigMap[row.designationId] || "-"}</td>
                <td className="p-2 border">{row.empType}</td>
                <td className="p-2 border">{row.empStatus}</td>

                <td className="p-2 border text-center">
                  <div className="flex justify-center gap-3">

                    <FaEye onClick={() => setPreviewData(row)} className="cursor-pointer text-purple-600" />
                    <FaEdit onClick={() => openEdit(row)} className="cursor-pointer text-blue-600" />
                    <FaUserPlus onClick={() => openOptionalFields(row)} className="cursor-pointer text-green-600" />
                    <FaTrash onClick={() => handleDelete(row)} className="cursor-pointer text-red-600" />

                  </div>
                </td>

              </tr>

            ))}

          </tbody>

        </table>

      </div>

      {/* ================= MOBILE VIEW ================= */}

      <div className="md:hidden space-y-3">

        {filteredData.map((row) => (

          <div key={row.empId} className="border rounded-xl p-4 shadow-sm bg-white">

            {/* HEADER WITH PHOTO */}
            <div className="flex justify-between items-center mb-2">

              <div className="flex items-center gap-3">

                {/* PHOTO / AVATAR */}
                {row.empPhoto ? (
                  <img
                    src={row.empPhoto}
                    alt="emp"
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center font-semibold">
                    {row.empName?.charAt(0).toUpperCase()}
                  </div>
                )}

                {/* NAME + MOBILE */}
                <div>
                  <h3 className="font-semibold text-slate-800">
                    {row.empName}
                  </h3>

                  <a
                    href={`tel:${row.empMobileNo}`}
                    className="flex items-center gap-1 text-xs text-blue-600"
                  >
                    <FaPhoneAlt className="text-green-600 text-xs" />
                    {row.empMobileNo}
                  </a>
                </div>

              </div>

              {/* STATUS */}
              <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                {row.empStatus}
              </span>

            </div>

            {/* DETAILS */}
            <p className="text-sm">
              <b>Restaurant:</b> {restMap[row.restId] || "-"}
            </p>

            <p className="text-sm">
              <b>Department:</b> {deptMap[row.departmentId] || "-"}
            </p>

            <p className="text-sm">
              <b>Designation:</b> {desigMap[row.designationId] || "-"}
            </p>

            <p className="text-sm">
              <b>Type:</b> {row.empType}
            </p>

            {/* ACTIONS */}
            <div className="flex justify-end gap-4 mt-3 text-lg">

              <FaEye
                onClick={() => setPreviewData(row)}
                className="cursor-pointer text-purple-600 hover:scale-110 transition"
              />

              <FaEdit
                onClick={() => openEdit(row)}
                className="cursor-pointer text-blue-600 hover:scale-110 transition"
              />

              <FaUserPlus
                onClick={() => openOptionalFields(row)}
                className="cursor-pointer text-green-600 hover:scale-110 transition"
              />

              <FaTrash
                onClick={() => handleDelete(row)}
                className="cursor-pointer text-red-600 hover:scale-110 transition"
              />

            </div>

          </div>

        ))}

      </div>

      {/* ================= MODALS ================= */}

      {formOpen && (
        <AddEditEmployeeMaster
          editingData={editing}
          onClose={() => setFormOpen(false)}
          onSuccess={fetchEmployees}
        />
      )}

      {quickOpen && (
        <AddeditQuickAdd
          onClose={() => setQuickOpen(false)}
          onSuccess={fetchEmployees}
        />
      )}

      {optionalOpen && (
        <AddEditEmployeeOptionalFields
          employeeData={optionalData}
          onClose={() => setOptionalOpen(false)}
          onSuccess={fetchEmployees}
        />
      )}

      <EmployeePreviewData
        data={previewData}
        deptMap={deptMap}
        desigMap={desigMap}
        restMap={restMap}
        onClose={() => setPreviewData(null)}
      />

    </div>
  );
};

export default EmployeeMaster;